import {
  Button,
  FlexBox,
  ModalBody,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import useDiscardChanges, {
  DiscardChangesFn,
} from 'articleEditor/hooks/useDiscardChanges';
import { LanguageId } from 'lib-knowledge-management-common/graphql';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useCallback } from 'react';

type DiscardChangesModalProps = {
  articleId: string;
  articleTitle: string;
  languageId: LanguageId;
  onDiscard?: (data: Awaited<ReturnType<DiscardChangesFn>>['data']) => void;
};

export function DiscardChangesModal({
  articleId,
  articleTitle,
  languageId,
  onDiscard,
}: DiscardChangesModalProps): JSX.Element {
  const { hideModal } = useModal();
  const { successNotify, errorNotify } = useNotification();
  const [discardFn, { loading }] = useDiscardChanges({
    articleId,
    languageId,
  });

  const discardChanges = useCallback(() => {
    discardFn()
      .then(({ data }) => {
        successNotify({
          title: 'Success',
          text: `The changes to “${articleTitle}” have been discarded.`,
        });
        onDiscard?.(data);
        hideModal();
      })
      .catch(() => {
        errorNotify({
          title: 'Error',
          text: `The changes to “${articleTitle}” could not be discarded. Please try again or contact support.`,
        });
      });
  }, [
    discardFn,
    successNotify,
    articleTitle,
    onDiscard,
    hideModal,
    errorNotify,
  ]);

  return (
    <ModalWrapper>
      <ModalHead title="Discard Changes" onClose={hideModal}></ModalHead>
      <ModalBody>
        <Text.Paragraph>
          Are you sure you want to discard changes to the latest version of{' '}
          <strong>“{articleTitle}”</strong>?
        </Text.Paragraph>
        <Text.Paragraph>This can not be undone.</Text.Paragraph>
        <FlexBox justifyContent="flex-end" gap="100">
          <Button disabled={loading} variant="tertiary" onClick={hideModal}>
            Cancel
          </Button>
          <Button variant="danger" disabled={loading} onClick={discardChanges}>
            {loading ? 'Discarding changes...' : 'Discard changes'}
          </Button>
        </FlexBox>
      </ModalBody>
    </ModalWrapper>
  );
}
